import '@utils/polyfills' // must be first: installs polyfills before dependent modules load

import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { I18nextProvider } from 'react-i18next'
import * as Sentry from '@sentry/react'

import i18n from '@locales/i18n'
import { routeTree } from './routeTree.gen'
import { RootProvider } from '@contexts/RootContext'
import { ApplicationProvider } from '@contexts/ApplicationContext'
import { Toaster } from '@components/ui/toast'
import AppErrorFallback, { RouteErrorComponent } from '@components/AppErrorFallback'

import '@/styles/globals.css'
import '@fontsource-variable/atkinson-hyperlegible-next'

import { initSentryAnonymousUser } from '@utils/sentryUser'
import { captureLoginHintToken } from '@utils/loginHintToken'
import { scrubBreadcrumb, scrubSentryEvent } from '@utils/sentryScrub'

// Lift the login hint token out of the URL fragment and blank it. Runs in every
// environment and ahead of Sentry.init, whose global-error and history handlers
// read location.href the moment they are installed.
captureLoginHintToken()

// Initialize Sentry
const APP_ENV = import.meta.env.VITE_APP_ENV

if (APP_ENV && ['sandbox', 'production'].includes(APP_ENV)) {
  Sentry.init({
    dsn: 'https://9f4c54966626a2bdaef6b87114f34a74@o4505519329116160.ingest.us.sentry.io/4507654214189056',
    environment: APP_ENV,
    tracesSampleRate: 1.0,
    // Structured logs (Sentry.logger.*) — used to document file-upload rejections
    // without raising an issue/alert per unsupported file.
    enableLogs: true,
    // Session replays disabled for patient data privacy
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubSentryEvent,
  })
  initSentryAnonymousUser()
}

// Create router. defaultErrorComponent reports route-render crashes to Sentry —
// TanStack Router otherwise swallows them into its own boundary, invisible to us.
const router = createRouter({ routeTree, defaultErrorComponent: RouteErrorComponent })

// Type registration
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <Sentry.ErrorBoundary fallback={({ eventId }) => <AppErrorFallback eventId={eventId} />}>
        <RootProvider rootElement={document.body}>
          <ApplicationProvider>
            <RouterProvider router={router} />
          </ApplicationProvider>
        </RootProvider>
      </Sentry.ErrorBoundary>
    </I18nextProvider>
    <Toaster />
  </React.StrictMode>
)
